import type { OnboardingStep } from '@/hooks/use-onboarding'
import { EMPLOYER_ORG_SETUP_ONBOARDING_STEP_IDS, EMPLOYER_ORG_SETUP_STEP_IDS } from '@/lib/employer-org-setup'
import { getFocusRoute } from '@/utils/focus-routes'

export const WELCOME_STEP_ID = 'welcome'

/** Employer account onboarding excludes org-scoped steps (handled in org setup wizard). */
export function employerAccountOnboardingSteps(
  steps: OnboardingStep[],
  groupName?: string,
): OnboardingStep[] {
  if (groupName !== 'employer') return steps
  return steps.filter((step) => !EMPLOYER_ORG_SETUP_ONBOARDING_STEP_IDS.has(step.id))
}

/** Steps that appear in "Step N of M" (welcome is step 0 and uncounted). */
export function countedOnboardingSteps(steps: OnboardingStep[]): OnboardingStep[] {
  return steps.filter((step) => step.id !== WELCOME_STEP_ID)
}

/** Completed steps that count toward progress (excludes welcome). */
export function countedCompletedOnboardingSteps(
  steps: OnboardingStep[],
  completedStepIds: string[],
): number {
  const countedIds = new Set(countedOnboardingSteps(steps).map((step) => step.id))
  return completedStepIds.filter((id) => countedIds.has(id)).length
}

export function onboardingProgressPercent(
  steps: OnboardingStep[],
  completedStepIds: string[],
): number {
  const total = countedOnboardingSteps(steps).length
  if (total === 0) return 0
  return Math.round((countedCompletedOnboardingSteps(steps, completedStepIds) / total) * 100)
}

export function onboardingProgressSummary(
  steps: OnboardingStep[],
  completedStepIds: string[],
): { completed: number; total: number; percent: number } {
  const total = countedOnboardingSteps(steps).length
  const completed = countedCompletedOnboardingSteps(steps, completedStepIds)
  const percent = total === 0 ? 0 : Math.round((completed / total) * 100)
  return { completed, total, percent }
}

export function onboardingStepLabel(
  currentStepIndex: number,
  steps: OnboardingStep[],
): { current: number; total: number } | null {
  const active = steps[currentStepIndex]
  if (!active || active.id === WELCOME_STEP_ID) return null

  const counted = countedOnboardingSteps(steps)
  const idx = counted.findIndex((step) => step.id === active.id)
  if (idx < 0) return null

  return { current: idx + 1, total: counted.length }
}

export function onboardingStepIndexById(steps: OnboardingStep[], stepId: string): number {
  return steps.findIndex((step) => step.id === stepId)
}

/**
 * Furthest step the user may open: the step after their highest completed index,
 * capped at the last step. Allows linear forward progress (e.g. profile → skills)
 * even when an optional intermediate step was not marked complete.
 */
export function maxOnboardingStepIndexFromProgress(
  steps: OnboardingStep[],
  completedStepIds: string[],
): number {
  if (steps.length === 0) return 0

  const completed = new Set(completedStepIds)
  let furthestCompleted = -1
  for (let i = 0; i < steps.length; i++) {
    if (completed.has(steps[i].id)) {
      furthestCompleted = i
    }
  }

  return Math.min(furthestCompleted + 1, steps.length - 1)
}

/** Clamp a requested step index to allowed progress (no jumping ahead). */
export function clampOnboardingStepIndex(
  steps: OnboardingStep[],
  completedStepIds: string[],
  requestedIndex: number,
): number {
  if (steps.length === 0 || requestedIndex < 0) return 0
  const maxIndex = maxOnboardingStepIndexFromProgress(steps, completedStepIds)
  return Math.min(requestedIndex, maxIndex)
}

/** Resume index when no ?step= param: first step not yet completed. */
export function onboardingResumeStepIndex(
  steps: OnboardingStep[],
  completedStepIds: string[],
): number {
  if (steps.length === 0) return 0

  const completed = new Set(completedStepIds)
  for (let i = 0; i < steps.length; i++) {
    if (!completed.has(steps[i].id)) {
      return i
    }
  }
  return steps.length - 1
}

/** Next step index after completing the step at `currentIndex` (linear wizard). */
export function nextOnboardingStepIndex(currentIndex: number, steps: OnboardingStep[]): number | null {
  if (currentIndex < 0 || currentIndex >= steps.length - 1) return null
  return currentIndex + 1
}

/** Groups where the final step still shows Back (employer posting preferences, etc.). */
const ONBOARDING_GROUPS_WITH_BACK_ON_LAST_STEP = new Set(['employer'])

/** Back on welcome, first counted step, and (for candidate) the last step. Employer last step keeps Back. */
export function onboardingCanGoBack(
  currentStepIndex: number,
  steps: OnboardingStep[],
  groupName?: string,
): boolean {
  if (steps.length === 0) return false
  if (currentStepIndex <= 0) return false

  const active = steps[currentStepIndex]
  const counted = countedOnboardingSteps(steps)
  if (active && counted[0]?.id === active.id) {
    return false
  }

  const isLast = currentStepIndex >= steps.length - 1
  if (isLast) {
    return groupName ? ONBOARDING_GROUPS_WITH_BACK_ON_LAST_STEP.has(groupName) : false
  }
  return true
}

/** Post-onboarding landing route: the dashboard of the focus named like the group. */
export function onboardingHomePathForGroup(groupName: string): string {
  return getFocusRoute(groupName || null)
}

export function onboardingDismissalStorageKey(email: string, groupName: string): string {
  return `onboarding_dismissed_${email}_${groupName}`
}

export function isOnboardingCelebrationDismissed(email: string, groupName: string): boolean {
  return localStorage.getItem(onboardingDismissalStorageKey(email, groupName)) === 'true'
}

export function dismissOnboardingCelebration(email: string, groupName: string): void {
  localStorage.setItem(onboardingDismissalStorageKey(email, groupName), 'true')
}

/** Setup card CTA: fresh users start; returning users continue. */
export function onboardingSetupCtaLabel(completedCount: number): 'Get started' | 'Continue' {
  return completedCount > 0 ? 'Continue' : 'Get started'
}

/** Homepage setup card should show (incomplete onboarding flow). */
export function isOnboardingSetupActive(
  status: { has_onboarding?: boolean; is_completed?: boolean } | null,
  isLoading: boolean,
): boolean {
  return !isLoading && Boolean(status?.has_onboarding && !status?.is_completed)
}

type EmployerOnboardingStatus = {
  has_onboarding?: boolean
  is_completed?: boolean
  group_name?: string
  completed_step_ids?: string[]
} | null | undefined

/** Org wizard finished bio/logo/prefs but account step `organisation_setup` was never marked. */
export function shouldBackfillEmployerOrganisationSetup(
  status: EmployerOnboardingStatus,
  options?: { skipWhenNamePending?: boolean },
): boolean {
  if (!status?.has_onboarding || status.is_completed || status.group_name !== 'employer') {
    return false
  }
  if (options?.skipWhenNamePending) return false
  const completed = new Set(status.completed_step_ids ?? [])
  if (completed.has('organisation_setup')) return false
  return EMPLOYER_ORG_SETUP_STEP_IDS.some((id) => completed.has(id))
}
